import { Card, CardContent } from '@/components/ui/card'
import { PageContainer } from '@/components/ui/page-container'
import { Skeleton } from '@/components/ui/skeleton'
import { cn } from '@/lib/utils'

export type EntityDetailSkeletonVariant =
  | 'profile'
  | 'job'
  | 'application'
  | 'settings'
  | 'compact'

export type EntityDetailSkeletonProps = {
  variant?: EntityDetailSkeletonVariant
  /** Include PageContainer wrapper (default true). */
  withPageContainer?: boolean
  /** Profile variant: add open-roles stubs in the main column (public org loading). */
  includeJobsSection?: boolean
  ariaLabel?: string
  className?: string
}

function ProfileBlocks({ includeJobsSection = false }: { includeJobsSection?: boolean }) {
  const aboutBlock = (
    <Card>
      <CardContent className="space-y-3 p-[var(--sp-card)]">
        <Skeleton className="h-4 w-24" />
        <Skeleton className="h-3 w-full" />
        <Skeleton className="h-3 w-5/6" />
        <Skeleton className="h-3 w-2/3" />
      </CardContent>
    </Card>
  )

  const rolesBlock = (
    <div className="space-y-3">
      <Skeleton className="h-6 w-28" />
      <Skeleton className="h-20 w-full rounded-xl" />
      <Skeleton className="h-20 w-full rounded-xl" />
    </div>
  )

  return (
    <>
      <Skeleton className="h-60 w-full rounded-xl" />
      <div className="space-y-3 pt-[72px]">
        <Skeleton className="h-8 w-1/2 max-w-sm" />
        <Skeleton className="h-4 w-1/3 max-w-xs" />
      </div>
      {includeJobsSection ? (
        <>
          {aboutBlock}
          {rolesBlock}
        </>
      ) : (
        aboutBlock
      )}
    </>
  )
}

function JobBlocks() {
  return (
    <>
      <div className="space-y-3">
        <Skeleton className="h-8 w-24" />
        <Skeleton className="h-7 w-2/3 max-w-md" />
        <Skeleton className="h-4 w-1/3 max-w-xs" />
      </div>
      <div className="grid grid-cols-1 gap-6 @3col:grid-cols-3">
        <div className="space-y-6 @3col:col-span-2">
          <Card>
            <CardContent className="space-y-3 p-[var(--sp-card)]">
              <Skeleton className="h-4 w-24" />
              <Skeleton className="h-3 w-full" />
              <Skeleton className="h-3 w-5/6" />
              <Skeleton className="h-3 w-2/3" />
            </CardContent>
          </Card>
          <Card>
            <CardContent className="space-y-3 p-[var(--sp-card)]">
              <Skeleton className="h-4 w-20" />
              <div className="flex gap-2">
                <Skeleton className="h-5 w-16" />
                <Skeleton className="h-5 w-20" />
                <Skeleton className="h-5 w-14" />
              </div>
            </CardContent>
          </Card>
        </div>
        <aside className="space-y-6">
          <Card>
            <CardContent className="space-y-3 p-[var(--sp-card)]">
              <Skeleton className="h-4 w-16" />
              <Skeleton className="h-20 w-full" />
              <Skeleton className="h-10 w-full" />
              <Skeleton className="h-10 w-full" />
            </CardContent>
          </Card>
        </aside>
      </div>
    </>
  )
}

function ApplicationBlocks() {
  return (
    <>
      <Skeleton className="h-8 w-32" />
      <Card>
        <CardContent className="space-y-4 p-[var(--sp-card)]">
          <Skeleton className="h-6 w-2/3 max-w-sm" />
          <Skeleton className="h-4 w-1/2" />
          <Skeleton className="h-5 w-24 rounded-md" />
          <Skeleton className="h-20 w-full" />
        </CardContent>
      </Card>
    </>
  )
}

function SettingsBlocks() {
  return (
    <div className="mx-auto w-full max-w-[640px] space-y-6">
      <div className="space-y-2">
        <Skeleton className="h-8 w-32" />
        <Skeleton className="h-4 w-2/3 max-w-sm" />
      </div>
      <div className="overflow-hidden rounded-md border">
        {Array.from({ length: 4 }).map((_, index) => (
          <div key={index} className="space-y-2 border-b border-border p-4 last:border-b-0">
            <Skeleton className="h-4 w-24" />
            <Skeleton className="h-10 w-full" />
          </div>
        ))}
      </div>
    </div>
  )
}

function CompactBlocks() {
  return (
    <div className="space-y-4">
      <Skeleton className="h-8 w-40" />
      <Skeleton className="h-28 w-full" />
      <Skeleton className="h-52 w-full" />
    </div>
  )
}

export function EntityDetailSkeleton({
  variant = 'profile',
  withPageContainer = true,
  includeJobsSection = false,
  ariaLabel = 'Loading details',
  className,
}: EntityDetailSkeletonProps) {
  const body = (
    <div
      className={cn('space-y-6', className)}
      role="status"
      aria-live="polite"
      aria-busy="true"
      aria-label={ariaLabel}
    >
      {variant === 'profile' ? <ProfileBlocks includeJobsSection={includeJobsSection} /> : null}
      {variant === 'job' ? <JobBlocks /> : null}
      {variant === 'application' ? <ApplicationBlocks /> : null}
      {variant === 'settings' ? <SettingsBlocks /> : null}
      {variant === 'compact' ? <CompactBlocks /> : null}
    </div>
  )

  if (!withPageContainer) {
    return body
  }

  return <PageContainer>{body}</PageContainer>
}

/** Organisation profile: banner + sections + jobs list stub. */
export function OrganisationProfileDetailSkeleton({
  ariaLabel = 'Loading organisation',
  className,
}: {
  ariaLabel?: string
  className?: string
}) {
  return (
    <PageContainer>
      <div
        className={cn('space-y-6', className)}
        role="status"
        aria-live="polite"
        aria-busy="true"
        aria-label={ariaLabel}
      >
        <ProfileBlocks includeJobsSection />
      </div>
    </PageContainer>
  )
}
